/**
 * Source Recommendation Popover Banner
 *
 * Compact banner shown at the top of the source control popover
 * when personalised source recommendations are available.
 * Shows country + specialty context, source favicons, and Apply/dismiss actions.
 *
 * Figma: https://www.figma.com/design/PiRSH2ORtexkp82fSPpi9Z/Evidence-sign-up---adoption?node-id=8447-76646
 */

import { seenModalIdsAtom } from '@/atoms/modalQueueAtoms';
import { useSourceRecommendationBanner } from '@/hooks/useSourceRecommendationBanner';
import { useIsSourceRecommendationPopoverBannerEnabled } from '@/app-shell/flags/evidence-flags';
import { getFaviconUrl } from '@/utils/faviconCache';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyCaptionBold } from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useAtomValue } from 'jotai';
import { X } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import { useIntl } from 'react-intl';

interface SourceRecommendationPopoverBannerProps {
  onClose?: () => void;
  className?: string;
}

function SourceFavicon({ domain }: { domain: string }) {
  const [hasError, setHasError] = useState(false);
  if (hasError) return null;
  return (
    <Image
      src={getFaviconUrl(domain)}
      alt=""
      width={24}
      height={24}
      unoptimized
      className="size-6 shrink-0 rounded-full"
      onError={() => setHasError(true)}
    />
  );
}

/**
 * Extract short label — same logic as the chat banner.
 */
function getShortLabel(displayName: string, websiteName: string | string[]): string {
  const name = typeof websiteName === 'string' ? websiteName : websiteName[0] ?? '';
  for (const candidate of [name, displayName]) {
    const match = candidate.match(/\(([A-Z][A-Za-z0-9-]{1,10})\)/);
    if (match?.[1]) return match[1];
  }
  if (displayName.length <= 12) return displayName;
  const leadingAcronym = displayName.match(/^([A-Z]{2,})(?=[A-Z][a-z])/);
  if (leadingAcronym?.[1]) return leadingAcronym[1];
  return displayName.slice(0, 10);
}

export function SourceRecommendationPopoverBanner({
  onClose,
  className,
}: SourceRecommendationPopoverBannerProps) {
  const intl = useIntl();
  const isPopoverBannerEnabled = useIsSourceRecommendationPopoverBannerEnabled();
  const seenModalIds = useAtomValue(seenModalIdsAtom);
  const {
    shouldShow: shouldShowBanner,
    sources,
    matchedCountryCode,
    specialtyName,
    isApplying,
    onApply,
    onDismiss,
  } = useSourceRecommendationBanner('popover');

  // Only show the Apply banner after the initial source recommendation banner
  // has been dismissed (GRO-2767)
  const hasSeenSourceRecommendation =
    seenModalIds?.has(ModalId.SOURCE_RECOMMENDATION_BANNER) ?? false;

  if (!isPopoverBannerEnabled || !shouldShowBanner || !hasSeenSourceRecommendation)
    return null;

  // Show first 3 source favicons, first 2 labels + "+N" for the rest
  const visibleFavicons = sources.slice(0, 3);
  const visibleLabels = sources.slice(0, 2);
  const remainingCount = Math.max(0, sources.length - 2);

  const title = specialtyName
    ? `${matchedCountryCode} ${specialtyName}`
    : matchedCountryCode;

  const sourcesSummary =
    visibleLabels.map((s) => getShortLabel(s.display_name, s.website_name)).join(', ') +
    (remainingCount > 0 ? ` +${remainingCount}` : '');

  const handleApply = async () => {
    await onApply();
    onClose?.();
  };

  const handleDismiss = () => {
    onDismiss();
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md bg-surface-accent px-2 py-2',
        className
      )}
      data-testid="source-recommendation-popover-banner"
    >
      {/* Source favicons stacked */}
      <div className="flex -space-x-1.5">
        {visibleFavicons.map((source) => (
          <SourceFavicon key={source.domain} domain={source.domain} />
        ))}
      </div>

      {/* Text content */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <TypographyCaptionBold className="truncate text-text-primary" title={title}>
          {title}
        </TypographyCaptionBold>
        <TypographyCaption className="truncate text-text-secondary" title={sourcesSummary}>
          {sourcesSummary}
        </TypographyCaption>
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          variant="default"
          size="xs"
          onClick={() => void handleApply()}
          disabled={isApplying}
        >
          {intl.formatMessage({ defaultMessage: 'Apply', id: 'EWw/tKINJT' })}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          iconOnly
          onClick={handleDismiss}
          aria-label={intl.formatMessage({ defaultMessage: 'Dismiss source recommendations', id: 'AXdO8ksm+g' })}
        >
          <X size={14} />
        </Button>
      </div>
    </div>
  );
}
